import { useState, useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ShieldCheck,
  ShieldAlert,
  Database,
  Search,
  X,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Table2,
  FileCode,
  Activity,
  Info,
  Lock,
  RefreshCw,
  KeyRound,
  ListFilter,
  GitBranch,
  ArrowUpRight,
  BookOpen,
} from "lucide-react";
import {
  canAccessDataMap,
  getDomainList,
  getCatalogSummaryMetrics,
  flattenDataDictionary,
  searchDataMap,
  getTableRelationships,
  getCoreRelationshipChain,
  getMigrationSummaries,
  getCatalogHealthMetrics,
  getSourceOfTruthBadge,
  getSensitivityBadge,
  getEntityStatusBadge,
  type DomainSummaryItem,
  type DictionaryFieldItem,
} from "./administrationDataMap";
import type { DomainId, SourceOfTruthClassification, DataSensitivity } from "@shared/data-catalog/types";

type QualityMetricView = {
  metricId: string;
  domain: string;
  name: string;
  status: "PASS" | "FAIL" | "UNKNOWN" | "CHECK_FAILED";
  severity: "OK" | "ATTENTION" | "CRITICAL" | "UNKNOWN";
  numerator: number | null;
  denominator: number | null;
  ratio: number | null;
  migrationRatio?: number | null;
  explanation: string;
  whyItMatters: string;
  canonicalSource: string;
  observedAt: string;
  errorCode?: string;
  breakdown?: Array<{ label: string; value: number }>;
  transition?: { element: string; futureAction: string };
};

function qualityStatusLabel(metric: QualityMetricView): string {
  switch (metric.status) {
    case "PASS":
      return "OK";
    case "FAIL":
      return metric.severity === "CRITICAL" ? "CRÍTICO" : "ATENCIÓN";
    case "CHECK_FAILED":
      return "CHECK_FAILED";
    default:
      return "DESCONOCIDO";
  }
}

function qualityStatusClass(metric: QualityMetricView): string {
  if (metric.status === "PASS") return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800";
  if (metric.status === "FAIL" && metric.severity === "CRITICAL") return "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800";
  if (metric.status === "FAIL") return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800";
  return "bg-zinc-50 text-zinc-700 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700";
}

function qualityCountLabel(metric: QualityMetricView): string {
  if (metric.numerator === null) return "—";
  if (metric.denominator === null) return `${metric.numerator}`;
  return `${metric.numerator} / ${metric.denominator}`;
}

function qualityRatioLabel(ratio: number | null | undefined): string {
  return ratio === null || ratio === undefined ? "—" : `${(ratio * 100).toFixed(1)} %`;
}

function qualityDomainLabel(domain: string): string {
  switch (domain) {
    case "BP_CUSTOMER":
      return "Business Partners y clientes";
    case "INSTALLATIONS_CONTRACTS":
      return "Contratos e instalaciones";
    case "BILLING":
      return "Billing";
    case "SUPPLIERS":
      return "Proveedores";
    case "MATERIALS":
      return "Materiales y catálogo";
    default:
      return domain;
  }
}

export default function AdministrationDataMap() {
  const { user, loading: authLoading } = useAuth();
  const isAuthorized = canAccessDataMap(user?.role);

  // tRPC query: strictly guarded, query executes only for authorized roles
  const catalogQuery = trpc.administration.dataMap.get.useQuery(undefined, {
    enabled: Boolean(isAuthorized && !authLoading),
    staleTime: 1000 * 60 * 30, // 30 minutes static cache
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // V1.1 runtime quality is an independent, aggregate-only read. It never
  // blocks the static catalog and it is enabled only after the same RBAC guard.
  const qualityQuery = trpc.administration.dataMap.quality.useQuery(undefined, {
    enabled: Boolean(isAuthorized && !authLoading),
    staleTime: 45_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const catalog = catalogQuery.data;

  // Active view tab state
  const [activeTab, setActiveTab] = useState<"mapa" | "diccionario" | "cambios" | "salud">("mapa");

  // Global search state
  const [searchQuery, setSearchQuery] = useState("");

  // Domain & table drill-down state
  const [selectedDomainId, setSelectedDomainId] = useState<DomainId>("entidades_clientes");
  const [selectedTableName, setSelectedTableName] = useState<string>("businessPartners");

  // Dictionary tab filter states
  const [dictDomainFilter, setDictDomainFilter] = useState<string>("all");
  const [dictSotFilter, setDictSotFilter] = useState<string>("all");
  const [dictSensitivityFilter, setDictSensitivityFilter] = useState<string>("all");
  const [dictNullFilter, setDictNullFilter] = useState<string>("all");
  const [dictSearchText, setDictSearchText] = useState("");

  // Computed data models
  const domainsList = useMemo(() => getDomainList(catalog), [catalog]);
  const summaryMetrics = useMemo(() => getCatalogSummaryMetrics(catalog), [catalog]);
  const flattenedDictionary = useMemo(() => flattenDataDictionary(catalog), [catalog]);
  const coreChain = useMemo(() => getCoreRelationshipChain(catalog), [catalog]);
  const migrationSummaries = useMemo(() => getMigrationSummaries(catalog), [catalog]);
  const healthMetrics = useMemo(() => getCatalogHealthMetrics(catalog), [catalog]);
  const qualityData = qualityQuery.data;
  const qualityChecks: QualityMetricView[] = qualityData?.checks ?? [];
  const transitionItems: QualityMetricView[] = qualityData?.transition.items ?? [];

  // Global search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || !catalog) return null;
    return searchDataMap(searchQuery, catalog);
  }, [searchQuery, catalog]);

  // Active selected table metadata
  const selectedTable = useMemo(() => {
    if (!catalog?.tables || !selectedTableName) return null;
    return catalog.tables[selectedTableName] ?? null;
  }, [catalog, selectedTableName]);

  // Relationships for selected table
  const selectedTableRelations = useMemo(() => {
    if (!catalog || !selectedTableName) return { outgoing: [], incoming: [] };
    return getTableRelationships(selectedTableName, catalog);
  }, [catalog, selectedTableName]);

  // Filtered dictionary items
  const filteredDictionary = useMemo(() => {
    return flattenedDictionary.filter((item: DictionaryFieldItem) => {
      if (dictDomainFilter !== "all" && item.domainId !== dictDomainFilter) {
        return false;
      }
      if (dictSotFilter !== "all" && item.sourceOfTruth !== dictSotFilter) {
        return false;
      }
      if (dictSensitivityFilter !== "all" && item.sensitivity !== dictSensitivityFilter) {
        return false;
      }
      if (dictNullFilter === "required" && item.isNullable) {
        return false;
      }
      if (dictNullFilter === "optional" && !item.isNullable) {
        return false;
      }
      if (dictSearchText.trim()) {
        const norm = dictSearchText.toLowerCase().trim();
        const matchesName = item.columnName.toLowerCase().includes(norm);
        const matchesHuman = item.columnHumanName.toLowerCase().includes(norm);
        const matchesTable = item.tableName.toLowerCase().includes(norm);
        const matchesDesc = item.description.toLowerCase().includes(norm);
        const matchesSyn = item.synonyms.some((s) => s.toLowerCase().includes(norm));
        if (!matchesName && !matchesHuman && !matchesTable && !matchesDesc && !matchesSyn) {
          return false;
        }
      }
      return true;
    });
  }, [
    flattenedDictionary,
    dictDomainFilter,
    dictSotFilter,
    dictSensitivityFilter,
    dictNullFilter,
    dictSearchText,
  ]);

  // Helper to drill-down into table
  const handleSelectTable = (domainId: DomainId, tableName: string) => {
    setSelectedDomainId(domainId);
    setSelectedTableName(tableName);
    setActiveTab("mapa");
    // Clear global search dropdown on explicit selection
    setSearchQuery("");
  };

  // Helper to jump to a table from search or relationship link
  const handleJumpToTable = (tableName: string) => {
    if (!catalog?.tables?.[tableName]) return;
    const table = catalog.tables[tableName];
    handleSelectTable(table.domain, tableName);
  };

  // 1. FORBIDDEN / ACCESS DENIED STATE
  if (!authLoading && !isAuthorized) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto py-12 px-4">
          <Card className="border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900 shadow-sm">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center mb-3">
                <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-xl text-red-950 dark:text-red-200">
                Acceso Restringido — Mapa de Datos
              </CardTitle>
              <CardDescription className="text-sm text-red-800/80 dark:text-red-300/80 max-w-lg mx-auto mt-2">
                El módulo de Gobernanza y Mapa de Datos está estrictamente restringido a los roles de
                Administración (<span className="font-semibold">admin</span> y{" "}
                <span className="font-semibold">admin_manager</span>).
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center text-xs text-muted-foreground pt-0 pb-6">
              Tu rol actual (<span className="font-mono">{user?.role || "no autenticado"}</span>) no
              dispone del permiso requerido{" "}
              <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-[11px]">
                administration.data_map.read
              </code>
              . Consulta con un administrador del sistema si necesitas acceso.
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // 2. LOADING STATE
  if (authLoading || (isAuthorized && catalogQuery.isLoading)) {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-pulse max-w-7xl mx-auto py-4">
          <div className="flex flex-col gap-2">
            <div className="h-8 bg-muted rounded w-64" />
            <div className="h-4 bg-muted rounded w-96" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-muted rounded-lg" />
            ))}
          </div>
          <div className="h-96 bg-muted rounded-lg" />
        </div>
      </DashboardLayout>
    );
  }

  // 3. BACKEND ERROR STATE
  if (catalogQuery.isError || !catalog) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto py-12 px-4">
          <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-900 shadow-sm">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center mb-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
              <CardTitle className="text-xl text-amber-950 dark:text-amber-200">
                Error al Cargar el Catálogo de Datos
              </CardTitle>
              <CardDescription className="text-sm text-amber-800/80 dark:text-amber-300/80 max-w-md mx-auto mt-2">
                {catalogQuery.error?.message ||
                  "No se pudo resolver el artefacto estático generado del catálogo de datos."}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center pt-2 pb-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => catalogQuery.refetch()}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reintentar Carga
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-w-0 w-full max-w-7xl mx-auto space-y-6 overflow-x-hidden pb-12">
        {/* ========================================================================= */}
        {/* HEADER SECTION                                                           */}
        {/* ========================================================================= */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 border-b pb-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                Mapa de Datos
              </h1>
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 gap-1.5 font-medium"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Solo lectura · metadatos y agregados
              </Badge>
              <Badge variant="outline" className="text-muted-foreground gap-1 text-xs">
                <Database className="w-3 h-3" />
                Catálogo v{catalog.schemaVersion}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground max-w-3xl">
              Catálogo estático, mapa semántico, diccionario técnico y calidad agregada de datos de la plataforma ODS Energy.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground hidden sm:inline">
              Generado: {new Date(catalog.generatedAt).toLocaleDateString("es-ES")}
            </span>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* PRIVACY & SECURITY BANNER                                                */}
        {/* ========================================================================= */}
        <div className="rounded-lg border border-blue-200 bg-blue-50/70 dark:bg-blue-950/30 dark:border-blue-900/60 p-3.5 flex items-start gap-3 text-xs text-blue-950 dark:text-blue-200">
          <Lock className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <p className="font-semibold text-blue-900 dark:text-blue-100">
              Sin datos de negocio / Solo metadatos y gobierno de datos
            </p>
            <p className="text-blue-800/90 dark:text-blue-300/90 leading-relaxed">
              Este módulo expone estructura estática, definiciones semánticas y contadores agregados read-only.
              El navegador nunca consulta ni almacena filas, valores económicos individuales ni PII confidencial.
            </p>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* GLOBAL SEARCH / FILTER BAR                                               */}
        {/* ========================================================================= */}
        <div className="relative">
          <div className="relative flex items-center">
            <Search className="w-4 h-4 absolute left-3 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Buscar en el catálogo (ej: 'CIF', 'precio contrato', 'businessPartners', 'titular')..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 h-10 text-sm shadow-xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-3 text-muted-foreground hover:text-foreground focus:outline-none"
                aria-label="Limpiar búsqueda"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Global Search Results Overlay/Card */}
          {searchQuery.trim() && (
            <Card className="mt-2 border-primary/30 shadow-md">
              <CardHeader className="py-2.5 px-4 bg-muted/40 border-b flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <Search className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs font-semibold text-foreground">
                    Resultados para &ldquo;{searchQuery}&rdquo;
                  </span>
                  <Badge variant="secondary" className="text-[11px] h-5 px-1.5 font-normal">
                    {searchResults?.totalMatches ?? 0} coincidencias
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2 text-muted-foreground"
                  onClick={() => setSearchQuery("")}
                >
                  Cerrar
                </Button>
              </CardHeader>
              <CardContent className="p-3 max-h-80 overflow-y-auto space-y-3">
                {searchResults && searchResults.totalMatches > 0 ? (
                  <>
                    {/* Matched Fields */}
                    {searchResults.fields.length > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                          Campos coincidentes ({searchResults.fields.length})
                        </div>
                        <div className="space-y-1.5">
                          {searchResults.fields.map((field) => (
                            <div
                              key={field.technicalPath}
                              onClick={() => handleSelectTable(field.domainId, field.tableName)}
                              className="p-2 rounded-md hover:bg-accent/60 cursor-pointer transition-colors border border-transparent hover:border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                            >
                              <div className="space-y-0.5 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-foreground">
                                    {field.columnHumanName}
                                  </span>
                                  <code className="text-primary font-mono text-[11px] bg-primary/10 px-1 py-0.5 rounded">
                                    {field.technicalPath}
                                  </code>
                                  <Badge variant="outline" className="text-[10px] h-4">
                                    {field.domainHumanName}
                                  </Badge>
                                </div>
                                {field.synonyms.length > 0 && (
                                  <p className="text-[11px] text-muted-foreground truncate">
                                    Sinónimos: {field.synonyms.join(", ")}
                                  </p>
                                )}
                                {field.description && (
                                  <p className="text-[11px] text-muted-foreground line-clamp-1">
                                    {field.description}
                                  </p>
                                )}
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs shrink-0 self-end sm:self-center gap-1"
                              >
                                Ver tabla
                                <ArrowRight className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Matched Tables */}
                    {searchResults.tables.length > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                          Tablas coincidentes ({searchResults.tables.length})
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {searchResults.tables.map((table) => (
                            <div
                              key={table.name}
                              onClick={() => handleSelectTable(table.domain, table.name)}
                              className="p-2 rounded-md hover:bg-accent/60 cursor-pointer transition-colors border border-transparent hover:border-border flex items-center justify-between text-xs"
                            >
                              <div className="min-w-0">
                                <div className="font-semibold text-foreground truncate">
                                  {table.humanName}
                                </div>
                                <code className="text-muted-foreground font-mono text-[11px]">
                                  {table.name}
                                </code>
                              </div>
                              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-2" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Matched Domains */}
                    {searchResults.domains.length > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                          Dominios coincidentes ({searchResults.domains.length})
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {searchResults.domains.map((dom) => (
                            <div
                              key={dom.id}
                              onClick={() => {
                                setSelectedDomainId(dom.id);
                                if (dom.tables.length > 0) {
                                  setSelectedTableName(dom.tables[0]);
                                }
                                setActiveTab("mapa");
                                setSearchQuery("");
                              }}
                              className="p-2 rounded-md hover:bg-accent/60 cursor-pointer transition-colors border border-transparent hover:border-border flex items-center justify-between text-xs"
                            >
                              <div className="min-w-0">
                                <div className="font-semibold text-foreground truncate">
                                  {dom.humanName}
                                </div>
                                <span className="text-[11px] text-muted-foreground">
                                  {dom.tableCount} tablas
                                </span>
                              </div>
                              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground shrink-0 ml-2" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="py-6 text-center text-xs text-muted-foreground space-y-1">
                    <p className="font-medium text-foreground">
                      No se encontraron resultados para &ldquo;{searchQuery}&rdquo;
                    </p>
                    <p>
                      Prueba buscando por sinónimos (ej: CIF, precio contrato, titular, dirección).
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* ========================================================================= */}
        {/* FOUR MAIN TABS                                                           */}
        {/* ========================================================================= */}
        <Tabs
          value={activeTab}
          onValueChange={(val) => setActiveTab(val as typeof activeTab)}
          className="min-w-0 space-y-6"
        >
          <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto p-1 bg-muted/60">
            <TabsTrigger value="mapa" className="gap-1.5 py-2">
              <Layers className="w-4 h-4" />
              <span>Mapa y Relaciones</span>
            </TabsTrigger>
            <TabsTrigger value="diccionario" className="gap-1.5 py-2">
              <BookOpen className="w-4 h-4" />
              <span>Diccionario de Datos</span>
            </TabsTrigger>
            <TabsTrigger value="cambios" className="gap-1.5 py-2">
              <FileCode className="w-4 h-4" />
              <span>Historial de Cambios</span>
            </TabsTrigger>
            <TabsTrigger value="salud" className="gap-1.5 py-2">
              <Activity className="w-4 h-4" />
              <span>Salud</span>
            </TabsTrigger>
          </TabsList>

          {/* ======================================================================= */}
          {/* TAB 1: MAPA Y RELACIONES                                                */}
          {/* ======================================================================= */}
          <TabsContent value="mapa" className="min-w-0 space-y-6 outline-none">
            {/* Top Summary Cards */}
            <div className="min-w-0 grid grid-cols-2 sm:grid-cols-4 gap-3.5">
              <Card className="shadow-xs">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">Dominios</p>
                    <p className="text-2xl font-bold text-foreground">
                      {summaryMetrics.totalDomains}
                    </p>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-950/50 flex items-center justify-center text-blue-600 dark:text-blue-400">
                    <Layers className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-xs">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">Tablas Catalogadas</p>
                    <p className="text-2xl font-bold text-foreground">
                      {summaryMetrics.totalTables}
                    </p>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-950/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                    <Table2 className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-xs">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">Columnas & Campos</p>
                    <p className="text-2xl font-bold text-foreground">
                      {summaryMetrics.totalColumns}
                    </p>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-amber-50 dark:bg-amber-950/50 flex items-center justify-center text-amber-600 dark:text-amber-400">
                    <KeyRound className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-xs">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium">Relaciones (FKs)</p>
                    <p className="text-2xl font-bold text-foreground">
                      {summaryMetrics.totalForeignKeys}
                    </p>
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-purple-50 dark:bg-purple-950/50 flex items-center justify-center text-purple-600 dark:text-purple-400">
                    <GitBranch className="w-5 h-5" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Core Relationship Chain Section */}
            <Card className="border-primary/20 bg-linear-to-r from-primary/5 via-background to-background shadow-xs">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-primary" />
                  <CardTitle className="text-base font-semibold">
                    Cadena Relacional Principal de Negocio
                  </CardTitle>
                </div>
                <CardDescription className="text-xs">
                  Trazabilidad canónica: Entidad / Cliente (businessPartners) ➔ Contrato Comercial
                  (customerContracts) ➔ Instalación Fotovoltaica (installations).
                </CardDescription>
              </CardHeader>
              <CardContent className="min-w-0 pt-0">
                <div className="min-w-0 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {coreChain.chain.map((step, idx) => {
                    const isStepSelected = selectedTableName === step.tableName;
                    return (
                      <div
                        key={step.tableName}
                        className={`min-w-0 rounded-lg border p-3.5 transition-all flex flex-col justify-between gap-3 ${
                          isStepSelected
                            ? "border-primary bg-primary/5 ring-1 ring-primary/40 shadow-xs"
                            : "border-border bg-card hover:border-primary/40"
                        }`}
                      >
                          <div className="min-w-0 space-y-1.5">
                            <div className="min-w-0 flex items-center justify-between">
                            <span className="text-[11px] font-semibold text-primary uppercase tracking-wider">
                              Paso {step.stepNumber}
                            </span>
                            <Badge variant="outline" className="min-w-0 max-w-full text-[10px] h-4 font-mono whitespace-normal break-all">
                              {step.tableName}
                            </Badge>
                          </div>
                          <h4 className="font-semibold text-sm text-foreground">
                            {step.entityName}
                          </h4>
                          <p className="text-xs text-muted-foreground leading-snug">
                            {step.description}
                          </p>
                        </div>

                        {step.connectionField && (
                          <div className="min-w-0 max-w-full break-all text-[11px] font-mono text-muted-foreground bg-muted/60 px-2 py-1 rounded">
                            FK: {step.tableName}.{step.connectionField} ➔ {step.targetTable}.{step.targetField}
                          </div>
                        )}

                        <Button
                          size="sm"
                          variant={isStepSelected ? "default" : "outline"}
                          className="w-full text-xs h-8 gap-1.5"
                          onClick={() => handleSelectTable(step.domainId, step.tableName)}
                        >
                          <span>Examinar {step.tableName}</span>
                          <ArrowRight className="w-3 h-3" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Domains Grid & Table Drill-down */}
            <div className="min-w-0 grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left Column: 13 Domains List */}
              <div className="min-w-0 lg:col-span-4 space-y-2.5">
                <div className="flex items-center justify-between px-1">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Dominios Semánticos (13)
                  </h3>
                  <span className="text-[11px] text-muted-foreground">
                    Selecciona para explorar
                  </span>
                </div>

                <div className="space-y-1.5 max-h-[600px] overflow-y-auto pr-1">
                  {domainsList.map((domain: DomainSummaryItem) => {
                    const isSelected = selectedDomainId === domain.id;
                    return (
                      <div
                        key={domain.id}
                        onClick={() => {
                          setSelectedDomainId(domain.id);
                          if (domain.tables.length > 0) {
                            setSelectedTableName(domain.tables[0]);
                          }
                        }}
                        className={`p-3 rounded-lg border text-left cursor-pointer transition-all ${
                          isSelected
                            ? "border-primary bg-primary/10 shadow-xs ring-1 ring-primary/30"
                            : "border-border bg-card hover:bg-accent/50 hover:border-primary/30"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="font-semibold text-xs text-foreground truncate">
                            {domain.humanName}
                          </span>
                          <Badge
                            variant={isSelected ? "default" : "secondary"}
                            className="text-[10px] h-4 px-1.5 shrink-0"
                          >
                            {domain.tableCount} {domain.tableCount === 1 ? "tabla" : "tablas"}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-muted-foreground line-clamp-2 leading-relaxed">
                          {domain.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Column: Tables in Selected Domain and Selected Table Details */}
              <div className="min-w-0 lg:col-span-8 space-y-4">
                {/* Tables Selector in Domain */}
                {catalog.domains[selectedDomainId] && (
                  <Card className="min-w-0 shadow-xs">
                    <CardHeader className="min-w-0 py-3.5 px-4 bg-muted/30 border-b">
                      <div className="min-w-0 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <CardTitle className="min-w-0 text-base font-semibold">
                              {catalog.domains[selectedDomainId]?.humanName}
                            </CardTitle>
                            <Badge variant="outline" className="max-w-full text-[10px] font-mono whitespace-normal break-all">
                              {selectedDomainId}
                            </Badge>
                          </div>
                          <CardDescription className="text-xs mt-1">
                            {catalog.domains[selectedDomainId]?.description}
                          </CardDescription>
                        </div>
                      </div>

                      {/* Tables Chips */}
                      <div className="min-w-0 flex flex-wrap gap-1.5 pt-2">
                        {catalog.domains[selectedDomainId]?.tables?.map((tblName) => {
                          const isTblSelected = selectedTableName === tblName;
                          return (
                            <Button
                              key={tblName}
                              size="sm"
                              variant={isTblSelected ? "default" : "outline"}
                              className="max-w-full h-7 text-xs font-mono gap-1 whitespace-normal break-all"
                              onClick={() => setSelectedTableName(tblName)}
                            >
                              <Table2 className="w-3 h-3" />
                              {tblName}
                            </Button>
                          );
                        })}
                      </div>
                    </CardHeader>

                    {/* Selected Table Details */}
                    {selectedTable ? (
                      <CardContent className="min-w-0 p-4 space-y-4">
                        {/* Table Header Details */}
                        <div className="min-w-0 flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b">
                          <div className="min-w-0 space-y-0.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="min-w-0 text-base font-bold text-foreground break-words">
                                {selectedTable.humanName}
                              </h4>
                              <code className="min-w-0 max-w-full text-xs font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground whitespace-normal break-all">
                                {selectedTable.name}
                              </code>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {selectedTable.description || "Sin descripción semántica adicional."}
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                            {(() => {
                              const sot = getSourceOfTruthBadge(selectedTable.sourceOfTruth);
                              const sens = getSensitivityBadge(selectedTable.sensitivity);
                              const status = getEntityStatusBadge(selectedTable.status);
                              return (
                                <>
                                  <Badge variant="outline" className={`text-[10px] ${sot.className}`}>
                                    {sot.label}
                                  </Badge>
                                  <Badge variant="outline" className={`text-[10px] ${sens.className}`}>
                                    {sens.label}
                                  </Badge>
                                  <Badge variant="outline" className={`text-[10px] ${status.className}`}>
                                    {status.label}
                                  </Badge>
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Key Concepts */}
                        {selectedTable.keyConcepts && selectedTable.keyConcepts.length > 0 && (
                          <div className="flex items-center gap-1.5 flex-wrap text-xs">
                            <span className="text-muted-foreground font-medium">Conceptos clave:</span>
                            {selectedTable.keyConcepts.map((concept, i) => (
                              <Badge key={i} variant="secondary" className="text-[11px] font-normal">
                                {concept}
                              </Badge>
                            ))}
                          </div>
                        )}

                        {/* Relationships Explorer for Selected Table */}
                        {(selectedTableRelations.outgoing.length > 0 ||
                          selectedTableRelations.incoming.length > 0) && (
                          <div className="min-w-0 max-w-full rounded-lg border bg-muted/20 p-3 space-y-2.5 overflow-hidden">
                            <div className="min-w-0 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                              <GitBranch className="w-3.5 h-3.5 text-primary" />
                              <span className="min-w-0 truncate">Relaciones de la tabla ({selectedTableName})</span>
                            </div>

                            <div className="min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                              {/* Outgoing */}
                              <div className="min-w-0">
                                <span className="text-[11px] font-semibold text-muted-foreground block mb-1">
                                  Referencias salientes (Depende de):
                                </span>
                                {selectedTableRelations.outgoing.length > 0 ? (
                                  <div className="space-y-1">
                                    {selectedTableRelations.outgoing.map((rel, i) => (
                                      <div
                                        key={i}
                                        className="min-w-0 flex items-center justify-between gap-2 p-1.5 rounded bg-background border text-[11px]"
                                      >
                                        <span className="font-mono truncate min-w-0 flex-1">
                                          {rel.sourceColumn} ➔ {rel.targetTable}.{rel.targetColumn}
                                        </span>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-5 px-1.5 text-[10px] text-ods-navigation shrink-0 hover:bg-accent"
                                          onClick={() => handleJumpToTable(rel.targetTable)}
                                        >
                                          Ir <ArrowUpRight className="w-2.5 h-2.5 ml-0.5" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-[11px]">
                                    Sin claves foráneas salientes.
                                  </span>
                                )}
                              </div>

                              {/* Incoming */}
                              <div className="min-w-0">
                                <span className="text-[11px] font-semibold text-muted-foreground block mb-1">
                                  Referencias entrantes (Tablas que la referencian):
                                </span>
                                {selectedTableRelations.incoming.length > 0 ? (
                                  <div className="space-y-1">
                                    {selectedTableRelations.incoming.map((rel, i) => (
                                      <div
                                        key={i}
                                        className="min-w-0 flex items-center justify-between gap-2 p-1.5 rounded bg-background border text-[11px]"
                                      >
                                        <span className="font-mono truncate min-w-0 flex-1">
                                          {rel.sourceTable}.{rel.sourceColumn} ➔ {rel.targetColumn}
                                        </span>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-5 px-1.5 text-[10px] text-ods-navigation shrink-0 hover:bg-accent"
                                          onClick={() => handleJumpToTable(rel.sourceTable)}
                                        >
                                          Ir <ArrowUpRight className="w-2.5 h-2.5 ml-0.5" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground text-[11px]">
                                    Ninguna otra tabla referencia esta clave primaria.
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Columns Table */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                              Columnas y Descriptores ({Object.keys(selectedTable.columns || {}).length})
                            </span>
                          </div>

                          <div className="min-w-0 max-w-full border rounded-md overflow-x-auto">
                            <Table>
                              <TableHeader className="bg-muted/40">
                                <TableRow className="text-xs">
                                  <TableHead className="py-2">Campo</TableHead>
                                  <TableHead className="py-2">Nombre Humano / Sinónimos</TableHead>
                                  <TableHead className="py-2">Tipo</TableHead>
                                  <TableHead className="py-2">Requerido</TableHead>
                                  <TableHead className="py-2">FK / Referencia</TableHead>
                                  <TableHead className="py-2">Sensibilidad</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody className="text-xs divide-y">
                                {Object.entries(selectedTable.columns || {}).map(([cName, col]) => (
                                  <TableRow key={cName} className="hover:bg-muted/30">
                                    <TableCell className="font-mono font-medium py-2 text-foreground">
                                      <div className="flex items-center gap-1.5">
                                        {col.isPrimaryKey && (
                                          <Badge
                                            variant="secondary"
                                            className="text-[9px] h-3.5 px-1 font-mono bg-amber-100 text-amber-900 border-amber-300"
                                          >
                                            PK
                                          </Badge>
                                        )}
                                        <span>{col.name || cName}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-2">
                                      <div className="space-y-0.5">
                                        <div className="font-medium text-foreground">
                                          {col.humanName || col.name}
                                        </div>
                                        {col.synonyms && col.synonyms.length > 0 && (
                                          <div className="text-[10px] text-muted-foreground truncate max-w-xs">
                                            {col.synonyms.join(", ")}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="font-mono text-[11px] text-muted-foreground py-2">
                                      {col.physicalType || col.normalizedType}
                                    </TableCell>
                                    <TableCell className="py-2">
                                      {col.isNullable ? (
                                        <span className="text-muted-foreground">Opcional</span>
                                      ) : (
                                        <span className="font-medium text-foreground">Obligatorio</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="py-2">
                                      {col.references ? (
                                        <button
                                          type="button"
                                          onClick={() => handleJumpToTable(col.references!.targetTable)}
                                          className="text-ods-navigation hover:underline font-mono text-[11px] flex items-center gap-0.5 text-left"
                                        >
                                          {col.references.targetTable}.{col.references.targetColumn}
                                          <ArrowUpRight className="w-2.5 h-2.5 shrink-0" />
                                        </button>
                                      ) : (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="py-2">
                                      {(() => {
                                        const sens = getSensitivityBadge(col.sensitivity);
                                        return (
                                          <Badge
                                            variant="outline"
                                            className={`text-[9px] h-4 px-1 ${sens.className}`}
                                          >
                                            {sens.label}
                                          </Badge>
                                        );
                                      })()}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>

                        {/* Structural Constraints Accordion / Block */}
                        {((selectedTable.indexes && selectedTable.indexes.length > 0) ||
                          (selectedTable.checks && selectedTable.checks.length > 0)) && (
                          <div className="min-w-0 pt-2 border-t text-xs text-muted-foreground space-y-2">
                            <div className="font-semibold text-foreground">
                              Restricciones estructurales & Índices:
                            </div>
                            <div className="min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {selectedTable.indexes?.map((idx, i) => (
                                <div
                                  key={i}
                                  className="min-w-0 max-w-full p-2 rounded bg-muted/40 font-mono text-[11px] flex flex-wrap items-center justify-between gap-2 overflow-hidden"
                                >
                                  <span className="min-w-0 max-w-full break-all">{idx.name}</span>
                                  <Badge variant="outline" className="min-w-0 max-w-full text-[9px] whitespace-normal break-all text-right">
                                    {idx.isUnique ? "UNIQUE" : "INDEX"} ({idx.columns.join(", ")})
                                  </Badge>
                                </div>
                              ))}
                              {selectedTable.checks?.map((chk, i) => (
                                <div
                                  key={i}
                                  className="min-w-0 max-w-full p-2 rounded bg-muted/40 font-mono text-[11px] flex flex-wrap items-center justify-between gap-2 overflow-hidden"
                                >
                                  <span className="min-w-0 max-w-full break-all">{chk.name}</span>
                                  <span className="min-w-0 max-w-full text-[10px] text-muted-foreground whitespace-normal break-all text-right">
                                    {chk.rawExpressionSummary}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    ) : (
                      <CardContent className="p-8 text-center text-xs text-muted-foreground">
                        Selecciona una tabla para ver su especificación.
                      </CardContent>
                    )}
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ======================================================================= */}
          {/* TAB 2: DICCIONARIO DE DATOS                                             */}
          {/* ======================================================================= */}
          <TabsContent value="diccionario" className="min-w-0 space-y-4 outline-none">
            <Card className="min-w-0 shadow-xs">
              <CardHeader className="min-w-0 py-4 px-4 border-b space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base font-bold">
                      Diccionario de Datos y Descriptores de Campo
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Inventario plano a nivel de campo con tipado físico/normalizado, fuente de verdad y
                      sensibilidad.
                    </CardDescription>
                  </div>
                  <Badge variant="secondary" className="text-xs self-start sm:self-auto">
                    {filteredDictionary.length} campos visibles
                  </Badge>
                </div>

                {/* Filter Controls Bar */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2.5 pt-1">
                  {/* Text search */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input
                      placeholder="Filtrar campos..."
                      value={dictSearchText}
                      onChange={(e) => setDictSearchText(e.target.value)}
                      className="h-8 pl-8 text-xs"
                    />
                  </div>

                  {/* Domain Selector */}
                  <Select value={dictDomainFilter} onValueChange={setDictDomainFilter}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Dominio" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">
                        Todos los dominios
                      </SelectItem>
                      {domainsList.map((d) => (
                        <SelectItem key={d.id} value={d.id} className="text-xs">
                          {d.humanName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Source of Truth Filter */}
                  <Select value={dictSotFilter} onValueChange={setDictSotFilter}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Fuente de Verdad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">
                        Toda Fuente de Verdad
                      </SelectItem>
                      <SelectItem value="canonical" className="text-xs">
                        Canónica
                      </SelectItem>
                      <SelectItem value="compatibility" className="text-xs">
                        Compatibilidad
                      </SelectItem>
                      <SelectItem value="legacy" className="text-xs">
                        Legacy
                      </SelectItem>
                      <SelectItem value="derived" className="text-xs">
                        Derivada
                      </SelectItem>
                      <SelectItem value="review" className="text-xs">
                        En Revisión
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Sensitivity Filter */}
                  <Select
                    value={dictSensitivityFilter}
                    onValueChange={setDictSensitivityFilter}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Sensibilidad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">
                        Toda Sensibilidad
                      </SelectItem>
                      <SelectItem value="public" className="text-xs">
                        Pública
                      </SelectItem>
                      <SelectItem value="internal" className="text-xs">
                        Interna
                      </SelectItem>
                      <SelectItem value="confidential" className="text-xs">
                        Confidencial
                      </SelectItem>
                      <SelectItem value="restricted" className="text-xs">
                        Restringida
                      </SelectItem>
                      <SelectItem value="pii" className="text-xs">
                        PII (Personal)
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Nullability Filter */}
                  <Select value={dictNullFilter} onValueChange={setDictNullFilter}>
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Obligatoriedad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">
                        Obligatorios y Opcionales
                      </SelectItem>
                      <SelectItem value="required" className="text-xs">
                        Solo Obligatorios
                      </SelectItem>
                      <SelectItem value="optional" className="text-xs">
                        Solo Opcionales
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                <div className="min-w-0 max-w-full overflow-x-auto max-h-[600px] overflow-y-auto">
                  <Table>
                    <TableHeader className="bg-muted/50 sticky top-0 z-10">
                      <TableRow className="text-xs">
                        <TableHead className="py-2.5">Identificador Técnico</TableHead>
                        <TableHead className="py-2.5">Nombre Humano & Sinónimos</TableHead>
                        <TableHead className="py-2.5">Dominio</TableHead>
                        <TableHead className="py-2.5">Tipo (Físico / Normalizado)</TableHead>
                        <TableHead className="py-2.5">Obligatorio</TableHead>
                        <TableHead className="py-2.5">Relación / FK</TableHead>
                        <TableHead className="py-2.5">Fuente de Verdad</TableHead>
                        <TableHead className="py-2.5">Sensibilidad</TableHead>
                        <TableHead className="py-2.5 min-w-[200px]">Explicación / Regla</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="text-xs divide-y">
                      {filteredDictionary.length > 0 ? (
                        filteredDictionary.map((field) => {
                          const sot = getSourceOfTruthBadge(field.sourceOfTruth);
                          const sens = getSensitivityBadge(field.sensitivity);
                          return (
                            <TableRow key={field.technicalPath} className="hover:bg-muted/30">
                              <TableCell className="font-mono py-2 text-foreground font-medium whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                  {field.isPrimaryKey && (
                                    <Badge
                                      variant="secondary"
                                      className="text-[9px] h-3.5 px-1 font-mono bg-amber-100 text-amber-900 border-amber-300"
                                    >
                                      PK
                                    </Badge>
                                  )}
                                  <span>{field.technicalPath}</span>
                                </div>
                              </TableCell>
                              <TableCell className="py-2">
                                <div className="space-y-0.5">
                                  <div className="font-semibold text-foreground">
                                    {field.columnHumanName}
                                  </div>
                                  {field.synonyms.length > 0 && (
                                    <div className="text-[10px] text-muted-foreground truncate max-w-xs">
                                      {field.synonyms.join(", ")}
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="py-2">
                                <Badge variant="outline" className="text-[10px] font-normal">
                                  {field.domainHumanName}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-[11px] text-muted-foreground py-2 whitespace-nowrap">
                                {field.physicalType} ({field.normalizedType})
                              </TableCell>
                              <TableCell className="py-2 whitespace-nowrap">
                                {field.isNullable ? (
                                  <span className="text-muted-foreground">Opcional</span>
                                ) : (
                                  <span className="font-semibold text-foreground">Obligatorio</span>
                                )}
                              </TableCell>
                              <TableCell className="py-2 font-mono text-[11px] whitespace-nowrap">
                                {field.references ? (
                                  <button
                                    type="button"
                                    onClick={() => handleJumpToTable(field.references!.targetTable)}
                                    className="text-ods-navigation hover:underline flex items-center gap-0.5 text-left"
                                  >
                                    {field.referencesDisplay}
                                    <ArrowUpRight className="w-2.5 h-2.5 shrink-0" />
                                  </button>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell className="py-2 whitespace-nowrap">
                                <Badge
                                  variant="outline"
                                  className={`text-[9px] h-4 px-1.5 ${sot.className}`}
                                >
                                  {sot.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2 whitespace-nowrap">
                                <Badge
                                  variant="outline"
                                  className={`text-[9px] h-4 px-1.5 ${sens.className}`}
                                >
                                  {sens.label}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-2 text-[11px] text-muted-foreground max-w-xs">
                                <div className="space-y-0.5">
                                  {field.description ? (
                                    <p className="line-clamp-2">{field.description}</p>
                                  ) : (
                                    <span className="italic text-muted-foreground/60">—</span>
                                  )}
                                  {field.businessRules && field.businessRules.length > 0 && (
                                    <div className="text-[10px] text-blue-600 dark:text-blue-400 font-mono">
                                      Regla: {field.businessRules[0]}
                                    </div>
                                  )}
                                  {field.notes && (
                                    <p className="text-[10px] text-amber-700 dark:text-amber-300">
                                      Gobernanza: {field.notes}
                                    </p>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            className="py-8 text-center text-xs text-muted-foreground"
                          >
                            No hay campos que coincidan con los filtros aplicados.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ======================================================================= */}
          {/* TAB 3: HISTORIAL DE CAMBIOS & MIGRACIONES                                */}
          {/* ======================================================================= */}
          <TabsContent value="cambios" className="min-w-0 space-y-4 outline-none">
            <Card className="min-w-0 shadow-xs">
              <CardHeader className="min-w-0 py-4 px-4 border-b">
                <div className="flex items-center gap-2">
                  <FileCode className="w-4 h-4 text-primary" />
                  <CardTitle className="text-base font-bold">
                    Historial de Migraciones y Cambios Estructurales
                  </CardTitle>
                </div>
                <CardDescription className="text-xs">
                  Resumen estructurado de las migraciones seguras del esquema registradas en el catálogo.
                </CardDescription>
              </CardHeader>
              <CardContent className="min-w-0 p-4 space-y-4">
                <div className="min-w-0 rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground flex items-start gap-2.5">
                  <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <div>
                    <span className="font-semibold text-foreground">
                      Nota de Seguridad y Gobernanza:
                    </span>{" "}
                    Los cuerpos ejecutables de SQL (DDL/DML) no son expuestos en la interfaz web por
                    principio de mínimo privilegio. Solo se reflejan metadatos de operaciones seguras
                    y tablas afectadas.
                  </div>
                </div>

                <div className="min-w-0 space-y-3">
                  {migrationSummaries.map((mig) => (
                    <div
                      key={mig.file}
                      className="min-w-0 rounded-lg border p-3.5 space-y-2.5 bg-card hover:border-primary/30 transition-colors"
                    >
                      <div className="min-w-0 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-2">
                        <div className="min-w-0 flex items-center gap-2">
                          <FileCode className="w-4 h-4 text-muted-foreground" />
                          <span className="min-w-0 max-w-full font-mono font-semibold text-xs text-foreground whitespace-normal break-all">
                            {mig.file}
                          </span>
                        </div>
                        <Badge variant="outline" className="min-w-0 max-w-full text-[10px] font-mono whitespace-normal break-all self-start sm:self-auto">
                          Tag: {mig.tag}
                        </Badge>
                      </div>

                      {/* Operations summary */}
                      {mig.safeOperationsSummary.length > 0 && (
                        <div className="space-y-1">
                          <span className="text-[11px] font-semibold text-muted-foreground block">
                            Operaciones declaradas ({mig.operationsCount}):
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {mig.safeOperationsSummary.map((op, idx) => (
                              <code
                                key={idx}
                                className="max-w-full text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded text-foreground border whitespace-normal break-all"
                              >
                                {op}
                              </code>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Affected tables */}
                      {mig.tablesAffected.length > 0 && (
                        <div className="space-y-1">
                          <span className="text-[11px] font-semibold text-muted-foreground block">
                            Tablas afectadas:
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {mig.tablesAffected.map((tbl) => (
                              <Badge
                                key={tbl}
                                variant="secondary"
                                className="max-w-full text-[10px] font-mono whitespace-normal break-all cursor-pointer hover:bg-primary/20"
                                onClick={() => handleJumpToTable(tbl)}
                              >
                                {tbl}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ======================================================================= */}
          {/* TAB 4: SALUD DEL MODELO, DATOS Y TRANSICIÓN                             */}
          {/* ======================================================================= */}
          <TabsContent value="salud" className="min-w-0 space-y-6 outline-none">
            <div className="rounded-lg border border-blue-200 bg-blue-50/70 dark:bg-blue-950/30 dark:border-blue-900/60 p-3.5 flex items-start gap-3 text-xs text-blue-950 dark:text-blue-200">
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
              <div className="space-y-0.5">
                <p className="font-semibold text-blue-900 dark:text-blue-100">
                  Salud separada en tres capas
                </p>
                <p className="text-blue-800/90 dark:text-blue-300/90 leading-relaxed">
                  Modelo/catálogo describe el esquema; Datos reales consulta únicamente agregados de PostgreSQL;
                  Transición legacy cuantifica dependencias pendientes. Esta vista no muestra filas, PII ni permite editar.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="shadow-xs">
                <CardHeader className="pb-2">
                  <CardDescription className="text-xs">Modelo / catálogo</CardDescription>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span>Catálogo cubierto al 100 %</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground pt-0">
                  {healthMetrics.assignedTablesCount} / {catalog.summary.totalTables} tablas asignadas a dominios.
                </CardContent>
              </Card>

              <Card className="shadow-xs">
                <CardHeader className="pb-2">
                  <CardDescription className="text-xs">Datos reales</CardDescription>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-600" />
                    <span>
                      {qualityQuery.isLoading
                        ? "Comprobando…"
                        : qualityQuery.isError || !qualityData
                          ? "DESCONOCIDO / CHECK_FAILED"
                          : qualityData.summary.status === "PASS"
                            ? "OK"
                            : qualityData.summary.status === "UNKNOWN"
                              ? "DESCONOCIDO"
                              : "ATENCIÓN"}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground pt-0">
                  {qualityQuery.isLoading
                    ? "Ejecutando checks agregados read-only."
                    : qualityData
                      ? `${qualityData.summary.passCount} OK · ${qualityData.summary.failCount} atención · ${qualityData.summary.checkFailedCount} fallidos`
                      : "No se pudo obtener el bloque de métricas; el catálogo sigue disponible."}
                </CardContent>
              </Card>

              <Card className="shadow-xs">
                <CardHeader className="pb-2">
                  <CardDescription className="text-xs">Transición legacy</CardDescription>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GitBranch className="w-5 h-5 text-amber-600" />
                    <span>
                      {!qualityData
                        ? "DESCONOCIDO"
                        : qualityData.transition.status === "PASS"
                          ? "OK"
                          : qualityData.transition.status === "UNKNOWN"
                            ? "DESCONOCIDO"
                            : "ATENCIÓN"}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-muted-foreground pt-0">
                  {qualityData
                    ? `${transitionItems.filter((item) => item.status === "FAIL").length} elementos con dependencia observada.`
                    : "Pendiente de comprobación runtime."}
                </CardContent>
              </Card>
            </div>

            <Card className="shadow-xs">
              <CardHeader className="py-3.5 px-4 border-b flex flex-row items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-sm font-bold">Datos reales — checks agregados</CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Solo contadores y porcentajes; última comprobación por check. Cache de 45 segundos.
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 shrink-0"
                  onClick={() => qualityQuery.refetch()}
                  disabled={qualityQuery.isFetching}
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${qualityQuery.isFetching ? "animate-spin" : ""}`} />
                  Actualizar
                </Button>
              </CardHeader>
              <CardContent className="p-4">
                {qualityQuery.isLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 animate-pulse">
                    {[1, 2, 3, 4].map((item) => <div key={item} className="h-28 rounded-lg bg-muted" />)}
                  </div>
                ) : qualityQuery.isError || !qualityData ? (
                  <div className="rounded-lg border border-zinc-200 bg-zinc-50 dark:bg-zinc-900/60 dark:border-zinc-800 p-4 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-zinc-800 dark:text-zinc-200">
                      <AlertTriangle className="w-4 h-4" />
                      UNKNOWN / CHECK_FAILED
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      El bloque runtime no está disponible. No se muestran datos parciales no verificables.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {["BP_CUSTOMER", "INSTALLATIONS_CONTRACTS", "BILLING", "SUPPLIERS", "MATERIALS"].map((domain) => {
                      const metrics = qualityChecks.filter((metric) => metric.domain === domain);
                      if (metrics.length === 0) return null;
                      return (
                        <section key={domain} className="space-y-3">
                          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {qualityDomainLabel(domain)}
                          </h3>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {metrics.map((metric) => (
                              <div key={metric.metricId} className="rounded-lg border p-3.5 space-y-2 bg-card">
                                <div className="flex items-start justify-between gap-3">
                                  <h4 className="text-sm font-semibold text-foreground leading-snug">{metric.name}</h4>
                                  <Badge variant="outline" className={`text-[10px] shrink-0 ${qualityStatusClass(metric)}`}>
                                    {qualityStatusLabel(metric)}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground leading-relaxed">{metric.explanation}</p>
                                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
                                  <span className="font-semibold text-foreground">Resultado: {qualityCountLabel(metric)}</span>
                                  {metric.denominator !== null && <span className="text-muted-foreground">Ratio: {qualityRatioLabel(metric.ratio)}</span>}
                                </div>
                                {metric.breakdown && metric.breakdown.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {metric.breakdown.map((item) => (
                                      <Badge key={item.label} variant="secondary" className="text-[10px]">
                                        {item.label}: {item.value}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                <p className="text-[11px] text-muted-foreground"><span className="font-semibold">Por qué importa:</span> {metric.whyItMatters}</p>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-[10px] text-muted-foreground">
                                  <span>Fuente: <code className="font-mono">{metric.canonicalSource}</code></span>
                                  <time dateTime={metric.observedAt}>Comprobado {new Date(metric.observedAt).toLocaleTimeString("es-ES")}</time>
                                </div>
                              </div>
                            ))}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="shadow-xs">
              <CardHeader className="py-3.5 px-4 border-b">
                <CardTitle className="text-sm font-bold">Transición legacy — evidencia agregada</CardTitle>
                <CardDescription className="text-xs mt-1">
                  Registros dependientes, porcentaje estimado de migración solo cuando el denominador es defendible y acción futura.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {transitionItems.length === 0 ? (
                  <div className="p-4 text-xs text-muted-foreground">UNKNOWN / CHECK_FAILED — no hay evidencia runtime disponible.</div>
                ) : (
                  <div className="min-w-0 max-w-full overflow-x-auto" data-testid="legacy-transition-table-wrapper">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Elemento</TableHead>
                          <TableHead className="text-xs">Estado</TableHead>
                          <TableHead className="text-xs">Dependientes</TableHead>
                          <TableHead className="text-xs">% migrado</TableHead>
                          <TableHead className="text-xs min-w-72">Acción futura</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transitionItems.map((metric) => (
                          <TableRow key={metric.metricId}>
                            <TableCell className="text-xs font-medium whitespace-normal">{metric.transition?.element ?? metric.name}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-[10px] ${qualityStatusClass(metric)}`}>
                                {qualityStatusLabel(metric)}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">{qualityCountLabel(metric)}</TableCell>
                            <TableCell className="text-xs">{qualityRatioLabel(metric.migrationRatio)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground whitespace-normal">{metric.transition?.futureAction ?? "Revisión requerida."}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="shadow-xs">
                <CardHeader className="pb-2"><CardDescription className="text-xs">Dominios activos</CardDescription><CardTitle className="text-lg flex items-center gap-2"><Layers className="w-5 h-5 text-blue-600" /><span>{healthMetrics.totalDomainsCount}</span></CardTitle></CardHeader>
                <CardContent className="text-xs text-muted-foreground pt-0">{healthMetrics.unassignedTablesCount} tablas sin asignar.</CardContent>
              </Card>
              <Card className="shadow-xs">
                <CardHeader className="pb-2"><CardDescription className="text-xs">Integridad estructural</CardDescription><CardTitle className="text-lg flex items-center gap-2"><GitBranch className="w-5 h-5 text-purple-600" /><span>{healthMetrics.foreignKeysCount} FKs</span></CardTitle></CardHeader>
                <CardContent className="text-xs text-muted-foreground pt-0">{healthMetrics.indexesCount} índices y {healthMetrics.checksCount} CHECK.</CardContent>
              </Card>
              <Card className="shadow-xs">
                <CardHeader className="pb-2"><CardDescription className="text-xs">Redacciones</CardDescription><CardTitle className="text-lg flex items-center gap-2"><Lock className="w-5 h-5 text-rose-600" /><span>{healthMetrics.totalRedactedColumnsCount}</span></CardTitle></CardHeader>
                <CardContent className="text-xs text-muted-foreground pt-0">Campos sensibles excluidos por política.</CardContent>
              </Card>
              <Card className="shadow-xs">
                <CardHeader className="pb-2"><CardDescription className="text-xs">Gobernanza</CardDescription><CardTitle className="text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-600" /><span>{healthMetrics.compatibilityTablesCount} bridges</span></CardTitle></CardHeader>
                <CardContent className="text-xs text-muted-foreground pt-0">{healthMetrics.reviewTablesCount} tablas en revisión y {healthMetrics.deprecatedTablesCount} deprecada.</CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
